import React, { useContext, useState } from "react";
import {
  Box,
  Drawer,
  IconButton,
  Typography,
  Collapse,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import ExpandLessRoundedIcon from "@mui/icons-material/ExpandLessRounded";
import { useNavigate, useLocation } from "react-router-dom";
import { useNavItems } from "./navItems";
import UserContext from "../../context/User";
import Logo from "../../assets/images/logo.png";

/**
 * Mobile slide-in drawer. Mirrors the desktop SideNav (same NAV_ITEMS source)
 * so the two stay in sync. Auth-gated rows still hide for logged-out users
 * and Get Help routes to /login if the user isn't logged in.
 */
function MobileNavDrawer({ open, onClose }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isLoggedIn } = useContext(UserContext);
  const navItems = useNavItems();
  const [expanded, setExpanded] = useState({});

  const isActive = (path) => location.pathname === path;

  const go = (path, getHelp) => {
    onClose && onClose();
    if (getHelp && !isLoggedIn) {
      navigate("/login");
      return;
    }
    navigate(path);
  };

  const renderRow = ({ label, icon: Icon, path, children, requiresAuth, getHelp, alsoOn }) => {
    if (requiresAuth && !isLoggedIn) return null;
    const active = children
      ? children.some((c) => isActive(c.path))
      : isActive(path) || (alsoOn || []).some(isActive);
    const isOpen = !!expanded[label];

    return (
      <Box key={label}>
        <Box
          onClick={() => {
            if (children) setExpanded((e) => ({ ...e, [label]: !e[label] }));
            else go(path, getHelp);
          }}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            px: 2,
            py: 1.6,
            mx: 1.25,
            borderRadius: "12px",
            cursor: "pointer",
            bgcolor: active ? "#fff5f5" : "transparent",
            border: active ? "1px solid #fee2e2" : "1px solid transparent",
            "&:hover": { bgcolor: "#f8fafc" },
          }}
        >
          {Icon && (
            <Icon sx={{ fontSize: 22, color: active ? "#CC0000" : "#475569" }} />
          )}
          <Typography
            sx={{
              flex: 1,
              fontWeight: active ? 700 : 600,
              fontSize: "0.95rem",
              color: active ? "#CC0000" : "#0f172a",
            }}
          >
            {label}
          </Typography>
          {children && (isOpen ? (
            <ExpandLessRoundedIcon sx={{ color: "#94a3b8" }} />
          ) : (
            <ExpandMoreRoundedIcon sx={{ color: "#94a3b8" }} />
          ))}
        </Box>

        {children && (
          <Collapse in={isOpen} unmountOnExit>
            {children.map((c) => {
              const ChildIcon = c.icon;
              const childActive = isActive(c.path);
              return (
                <Box
                  key={c.label}
                  onClick={() => go(c.path)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.25,
                    pl: 5,
                    pr: 2,
                    py: 1.1,
                    mx: 1.25,
                    borderRadius: "10px",
                    cursor: "pointer",
                    bgcolor: childActive ? "#fff5f5" : "transparent",
                    "&:hover": { bgcolor: "#f8fafc" },
                  }}
                >
                  {ChildIcon && (
                    <ChildIcon sx={{ fontSize: 18, color: childActive ? "#CC0000" : "#64748b" }} />
                  )}
                  <Typography
                    sx={{
                      fontSize: "0.88rem",
                      fontWeight: childActive ? 700 : 500,
                      color: childActive ? "#CC0000" : "#334155",
                    }}
                  >
                    {c.label}
                  </Typography>
                </Box>
              );
            })}
          </Collapse>
        )}
      </Box>
    );
  };

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { width: 280, bgcolor: "#fff" },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2, py: 1.5, borderBottom: "1px solid #f1f5f9" }}>
        <img src={Logo} alt="RushmyFood" style={{ height: 36, objectFit: "contain" }} />
        <IconButton onClick={onClose} size="small" sx={{ color: "#475569" }}>
          <CloseRoundedIcon />
        </IconButton>
      </Box>
      <Box sx={{ flex: 1, py: 1.25, overflowY: "auto" }}>
        {navItems.map(renderRow)}
      </Box>
    </Drawer>
  );
}

export default MobileNavDrawer;
